// src/pages/Reviews.jsx
import React, { useState, useEffect } from 'react';
import { fetchAllReviews } from '../services/reviewsAPI';
import '../styles/Reviews.css';

// ─── Star Display ────────────────────────────────────────────────────────────
const StarDisplay = ({ rating, size = 'md' }) => (
  <div className={`star-display star-display--${size}`} aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((star) => (
      <svg
        key={star}
        className={`star-svg ${star <= rating ? 'star-filled' : 'star-empty'}`}
        viewBox="0 0 24 24"
        fill={star <= rating ? '#FFD60A' : 'none'}
        stroke={star <= rating ? '#FFD60A' : '#d1d5db'}
        strokeWidth="1.5"
      >
        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
      </svg>
    ))}
  </div>
);

// ─── Review Card ─────────────────────────────────────────────────────────────
const ReviewCard = ({ review, index }) => {
  const name = review.user?.name || review.user?.email?.split('@')[0] || 'Anonymous';
  const initial = name.charAt(0).toUpperCase();
  const date = new Date(review.created_at).toLocaleDateString('en-IN', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
  const carName = review.car
    ? `${review.car.brand || ''} ${review.car.model || ''}`.trim()
    : null;
  const city = review.user?.city || review.car?.location || null;

  // Map category to a human-readable label
  const CATEGORY_LABELS = {
    customer_service:     'Customer Service',
    quality_of_cars:      'Quality of Cars',
    value_added_services: 'Value Added Services',
  };
  const categoryLabel = review.category ? CATEGORY_LABELS[review.category] : null;

  return (
    <div className="review-card" style={{ '--delay': `${index * 0.06}s` }}>
      <div className="review-card__header">
        <div className="review-avatar"><span>{initial}</span></div>
        <div className="review-meta">
          <span className="review-name">{name}</span>
          <span className="review-date-city">
            {date}
            {city && <span className="review-city"> · {city}</span>}
          </span>
        </div>
        <div className="review-badge">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="#2e7d52" strokeWidth="2.5">
            <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="12" r="10"/>
          </svg>
          Verified
        </div>
      </div>

      <div className="review-card__stars">
        <StarDisplay rating={review.rating} />
        <span className="review-rating-num">{review.rating}.0</span>
        {/* Show category tag inline with stars for buyer reviews */}
        {categoryLabel && (
          <span className="review-category-tag">{categoryLabel}</span>
        )}
      </div>

      <p className="review-comment">{review.comment}</p>

      {carName && (
        <div className="review-car-tag">
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="1" y="3" width="15" height="13" rx="1"/>
            <path d="M16 8h4l3 3v5h-7V8z"/>
            <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
          </svg>
          {carName}
        </div>
      )}
    </div>
  );
};

// ─── Rating Summary ───────────────────────────────────────────────────────────
const RatingSummary = ({ reviews }) => {
  if (!reviews.length) return null;
  const avg = (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1);
  const counts = [5, 4, 3, 2, 1].map((n) => ({
    star: n,
    count: reviews.filter((r) => r.rating === n).length,
  }));
  return (
    <div className="rating-summary">
      <div className="rating-summary__score">
        <span className="rating-big">{avg}</span>
        <StarDisplay rating={Math.round(Number(avg))} size="lg" />
        <span className="rating-count">{reviews.length} review{reviews.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="rating-summary__bars">
        {counts.map(({ star, count }) => (
          <div key={star} className="rating-bar-row">
            <span className="rating-bar-label">{star}</span>
            <svg viewBox="0 0 12 12" width="10" height="10" fill="#FFD60A">
              <polygon points="6,1 7.55,4.13 11,4.63 8.5,7.07 9.09,10.51 6,8.88 2.91,10.51 3.5,7.07 1,4.63 4.45,4.13"/>
            </svg>
            <div className="rating-bar-track">
              <div
                className="rating-bar-fill"
                style={{ width: `${(count / reviews.length) * 100}%` }}
              />
            </div>
            <span className="rating-bar-count">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Category filter config ────────────────────────────────────────────────────
// Only shown on the "From Buyer" tab — Seller tab has no filters (like Spinny)
const BUYER_FILTERS = [
  { label: 'All Reviews',          value: 'all'                  },
  { label: 'Customer Service',     value: 'customer_service'     },
  { label: 'Quality of Cars',      value: 'quality_of_cars'      },
  { label: 'Value Added Services', value: 'value_added_services' },
];

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Reviews() {
  const [tab, setTab]         = useState('buyer');   // 'buyer' | 'seller'
  const [category, setCategory] = useState('all');   // buyer filter
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => { loadReviews(); }, []);

  const loadReviews = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAllReviews();
      setReviews(Array.isArray(data) ? data : data.reviews || []);
    } catch (err) {
      setError('Unable to load reviews. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 1: split by buyer / seller ──
  // review_type === 'buyer' → buyer tab
  // review_type === 'seller' → seller tab
  // review_type is null/undefined → show in both (fallback)
  const tabReviews = reviews.filter((r) => {
    if (!r.review_type) return true;
    return r.review_type === tab;
  });

  // ── Step 2: apply category filter (buyer tab only) ──
  const displayReviews =
    tab === 'buyer' && category !== 'all'
      ? tabReviews.filter((r) => r.category === category)
      : tabReviews;

  // Reset category when switching to seller tab
  const handleTabChange = (t) => {
    setTab(t);
    if (t === 'seller') setCategory('all');
  };

  return (
    <div className="reviews-page">

      {/* ── Hero ── */}
      <div className="reviews-hero">
        <div className="reviews-hero__content">
          <h1 className="reviews-hero__title">Customer Reviews</h1>
          <p className="reviews-hero__sub">
            Real experiences from buyers and sellers across our 5 cities.
          </p>
        </div>
      </div>

      <div className="reviews-body">

        {/* ── Tabs ── */}
        <div className="reviews-tabs">
          {[
            { key: 'buyer',  label: 'From Buyer'  },
            { key: 'seller', label: 'From Seller' },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`reviews-tab ${tab === key ? 'reviews-tab--active' : ''}`}
              onClick={() => handleTabChange(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── Category Filter Pills — BUYER ONLY ── */}
        {tab === 'buyer' && (
          <div className="reviews-filters">
            {BUYER_FILTERS.map(({ label, value }) => (
              <button
                key={value}
                className={`filter-pill ${category === value ? 'filter-pill--active' : ''}`}
                onClick={() => setCategory(value)}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* ── Rating Summary ── */}
        {!loading && !error && displayReviews.length > 0 && (
          <RatingSummary reviews={displayReviews} />
        )}

        {/* ── Loading Skeletons ── */}
        {loading && (
          <div className="reviews-loading">
            {[1, 2, 3].map((i) => (
              <div key={i} className="review-skeleton">
                <div className="skeleton-avatar" />
                <div className="skeleton-lines">
                  <div className="skeleton-line skeleton-line--short" />
                  <div className="skeleton-line skeleton-line--stars" />
                  <div className="skeleton-line" />
                  <div className="skeleton-line skeleton-line--long" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Error ── */}
        {error && (
          <div className="reviews-error">
            <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="#FFD60A" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <p>{error}</p>
            <button onClick={loadReviews} className="retry-btn">Try Again</button>
          </div>
        )}

        {/* ── Empty ── */}
        {!loading && !error && displayReviews.length === 0 && (
          <div className="reviews-empty">
            <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="#FFD60A" strokeWidth="1.2">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
            </svg>
            <p>
              No {tab === 'buyer' ? 'buyer' : 'seller'} reviews
              {tab === 'buyer' && category !== 'all'
                ? ` for "${BUYER_FILTERS.find(f => f.value === category)?.label}"`
                : ''} yet.
            </p>
            <span className="reviews-empty__hint">
              Reviews are shared by customers after completing a transaction.
            </span>
          </div>
        )}

        {/* ── Reviews List ── */}
        {!loading && !error && displayReviews.length > 0 && (
          <div className="reviews-grid">
            {displayReviews.map((review, i) => (
              <ReviewCard key={review.id} review={review} index={i} />
            ))}
          </div>
        )}

      </div>
    </div>
  );
}